import React, { createContext, useContext, useState, useEffect } from "react";
import { api, authService } from "../services";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      "useAuth doit être utilisé à l'intérieur d'un AuthProvider",
    );
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(
    localStorage.getItem("token") || null,
  );
  const [isAdminSession, setIsAdminSession] = useState(
    localStorage.getItem("isAdminSession") === "true"
  );
  // Le refresh token est géré par cookies HttpOnly côté backend
  // Plus besoin de le stocker dans le state

  // Écouteur pour l'expiration des tokens depuis l'apiClient
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log("[AuthContext] Token expiré détecté depuis apiClient");
      setUser(null);
      setAuthToken(null);
      setLoading(false);
      toast.error("Votre session a expiré. Veuillez vous reconnecter.");
    };

    const handleSessionExpired = (event) => {
      console.log("[AuthContext] Session expirée détectée:", event.detail);
      setUser(null);
      setAuthToken(null);
      setLoading(false);
      // Pas de toast ici car déjà affiché par l'apiClient
    };

    window.addEventListener('authTokenExpired', handleTokenExpired);
    window.addEventListener('authSessionExpired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('authTokenExpired', handleTokenExpired);
      window.removeEventListener('authSessionExpired', handleSessionExpired);
    };
  }, []);

  // Vérifier l'état d'authentification au chargement
  // AMÉLIORATION: Gestion robuste du refresh (Ctrl+R / Ctrl+Shift+R)
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const MAX_RETRIES = 2;

    const verifyAuth = async () => {
      const currentToken = localStorage.getItem("token");
      console.log("[AuthContext] Exécution de verifyAuth. Token:", currentToken ? "Oui" : "Non");

      // Si pas de token, on ne fait rien
      if (!currentToken) {
        if (isMounted) setLoading(false);
        return;
      }

      setAuthToken(currentToken);
      
      // Fonction de vérification avec retry
      const attemptVerification = async () => {
        try {
          console.log(`[AuthContext] Vérification avec /auth/me (tentative ${retryCount + 1})`);
          const { data } = await authService.getProfile();
          
          if (isMounted && data && data.user) {
            console.log("[AuthContext] Utilisateur authentifié:", data.user);
            setUser(data.user);
            setIsAdminSession(data.user.role === 'admin');
            // Sauvegarder aussi dans sessionStorage pour plus de résilience
            sessionStorage.setItem('userCache', JSON.stringify(data.user));
            return true;
          }
          return false;
        } catch (error) {
          console.error(`[AuthContext] Tentative ${retryCount + 1} échouée:`, error.message);
          
          // Si c'est une erreur réseau (pas 401), on peut réessayer
          const isNetworkError = !error.response;
          const is401 = error.response?.status === 401;
          
          if (isNetworkError && retryCount < MAX_RETRIES) {
            retryCount++;
            // Attendre un peu avant de réessayer (surtout utile après hard refresh)
            await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
            return attemptVerification();
          }
          
          // Pour les erreurs 401, l'intercepteur essaie déjà le refresh token
          // Donc on ne fait rien ici, l'événement authSessionExpired sera émis si nécessaire
          if (is401) {
            console.log("[AuthContext] Erreur 401, l'intercepteur gère le refresh token");
          }
          
          return false;
        }
      };

      const success = await attemptVerification();
      
      // Si l'authentification a échoué mais qu'on a un cache en session, l'utiliser temporairement
      if (!success && isMounted) {
        const cachedUser = sessionStorage.getItem('userCache');
        if (cachedUser) {
          try {
            const parsedUser = JSON.parse(cachedUser);
            console.log("[AuthContext] Utilisation du cache utilisateur temporaire");
            setUser(parsedUser);
            setIsAdminSession(parsedUser.role === 'admin');
          } catch (e) {
            console.error("[AuthContext] Erreur parsing cache utilisateur");
          }
        }
      }

      if (isMounted) {
        setLoading(false);
      }
    };

    verifyAuth();

    return () => {
      isMounted = false;
    };
  }, []); // Exécuter une seule fois au montage du composant

  const adminLogin = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post("/admin/login", { email, password });
      const { token, user: adminData } = response.data;

      if (!adminData || adminData.role !== 'admin') {
        throw new Error("Les données reçues ne correspondent pas à un compte administrateur.");
      }

      localStorage.setItem("token", token);
      localStorage.setItem("isAdminSession", "true");
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setAuthToken(token);
      setUser(adminData);
      setIsAdminSession(true);
      toast.success("Connexion administrateur réussie !");

      return adminData;
    } catch (error) {
      console.error("Erreur de connexion admin:", error);
      const errorMessage = error.response?.data?.message || "Erreur de connexion administrateur";
      setError(errorMessage);
      toast.error(errorMessage);
      throw error; // Renvoyer l'erreur pour que le composant puisse la gérer
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Tentative de connexion avec:", { email, password: "***" });

      const response = await api.post("/auth/login", { email, password });
      console.log("Réponse du serveur:", response.data);

      const {
        token,
        user: userData,
      } = response.data;

      if (!userData) {
        console.error("Aucune donnée utilisateur reçue");
        setError("Erreur de connexion: aucune donnée utilisateur");
        toast.error("Erreur de connexion: aucune donnée utilisateur");
        return false;
      }

      console.log("Données utilisateur:", userData);

      // Sauvegarder le token et l'utilisateur
      localStorage.setItem("token", token);
      localStorage.removeItem("isAdminSession"); // Assurer qu'il ne s'agit pas d'une session admin

      // Mettre à jour l'en-tête d'autorisation global
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setAuthToken(token);
      setUser(userData);
      setIsAdminSession(false);
      
      // Sauvegarder en cache pour la résilience au refresh
      sessionStorage.setItem('userCache', JSON.stringify(userData));
      
      toast.success("Connexion réussie !");

      // Retourner l'utilisateur connecté
      return userData;
    } catch (error) {
      console.error("Erreur de connexion complète:", error);
      const errorMessage =
        error.response?.data?.message || "Erreur de connexion";
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (authToken) {
        await api.post("/auth/logout");
      }
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    } finally {
      // Nettoyer l'en-tête d'autorisation global
      delete api.defaults.headers.common['Authorization'];
      
      // Nettoyer localStorage ET sessionStorage
      localStorage.removeItem("token");
      localStorage.removeItem("isAdminSession");
      sessionStorage.removeItem("userCache"); // Nettoyer le cache utilisateur
      
      // Le refresh token est géré par cookies HttpOnly côté backend
      setAuthToken(null);
      setUser(null);
      setIsAdminSession(false);
      toast.info("Déconnexion réussie");
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Tentative d'inscription avec données:", {
        ...userData,
        password: userData.password ? "***" : undefined,
      });

      const response = await api.post("/auth/register", userData);
      console.log("Réponse d'inscription:", {
        ...response.data,
        token: response.data.token ? "token présent" : "token absent",
      });

      const { token, user: newUser } = response.data;

      if (token) {
        // Sauvegarder le token si l'utilisateur est automatiquement vérifié (en dev)
        if (newUser && newUser.isVerified) {
          localStorage.setItem("token", token);
          localStorage.setItem("user", JSON.stringify(newUser));

          setAuthToken(token);
          setUser(newUser);
        }
      }

      toast.success("Inscription réussie !");

      return true;
    } catch (error) {
      console.error("Erreur d'inscription:", error);
      setError(error.response?.data?.message || "Erreur lors de l'inscription");
      toast.error(
        error.response?.data?.message || "Erreur lors de l'inscription",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Tentative de vérification du token:", token);

      const response = await api.get(`/auth/verify-email/${token}`);
      console.log("Réponse de vérification d'email:", response.data);

      if (response.data && response.data.verified) {
        toast.success(
          "Email vérifié avec succès. Vous pouvez maintenant vous connecter.",
        );
        return true;
      }

      return false;
    } catch (error) {
      console.error("Erreur lors de la vérification de l'email:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors de la vérification de l'email";
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Tentative de renvoi d'email de vérification à:", email);

      const response = await api.post("/auth/resend-verification", { email });
      console.log("Réponse de renvoi d'email:", response.data);

      toast.success("Email de vérification renvoyé avec succès.");
      return true;
    } catch (error) {
      console.error("Erreur lors du renvoi de l'email de vérification:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors du renvoi de l'email de vérification";
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);

      await api.post("/auth/forgot-password", { email });

      toast.success(
        "Si votre email existe dans notre base de données, vous recevrez un lien de réinitialisation.",
      );
      return true;
    } catch (error) {
      console.error("Erreur lors de la demande de réinitialisation:", error);
      // Pour des raisons de sécurité, nous montrons toujours un message générique
      toast.success(
        "Si votre email existe dans notre base de données, vous recevrez un lien de réinitialisation.",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, password, confirmPassword) => {
    try {
      setLoading(true);
      setError(null);

      await api.post("/auth/reset-password", {
        token,
        password,
        confirmPassword,
      });

      toast.success(
        "Mot de passe réinitialisé avec succès. Vous pouvez maintenant vous connecter.",
      );
      return true;
    } catch (error) {
      console.error(
        "Erreur lors de la réinitialisation du mot de passe:",
        error,
      );
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors de la réinitialisation du mot de passe";
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const refreshAuthToken = async () => {
    try {
      // Le refresh token est géré par cookies HttpOnly côté backend
      const response = await api.post("/auth/refresh-token");

      if (response.data && response.data.token) {
        localStorage.setItem("token", response.data.token);
        setAuthToken(response.data.token);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Erreur lors du rafraîchissement du token:", error);
      return false;
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin" && isAdminSession,
    authToken,
    adminLogin,
    login,
    logout,
    register,
    verifyEmail,
    resendVerificationEmail,
    forgotPassword,
    resetPassword,
    refreshAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
